import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  fade,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Add,
  MoreVert,
  Edit,
  Check,
  Cancel,
} from "@material-ui/icons";
import Title from "../../ui/Title";
import { addMonths, format } from "date-fns";
import {
  RESERVATIONS_CLASS_TYPES,
  RESERVATIONS_CLASS_TYPES_MIDDLE,
  RESERVATIONS_GROUP_TYPES,
  RESERVATIONS_SCHOOL_TYPES,
  RESERVATIONS_STATUS,
  RESERVATIONS_VISITOR_TYPES_RESTRICTED,
  RESERVATIONS_VISIT_LANGUAGES,
} from "../../utils/ecommerceGenConstants";
import { ecommerce } from "../../messages/ecommerce";
import { form } from "../../messages/form";
import { useIntl } from "react-intl";
import { DatePicker } from "../../ui/DatePicker";
import { common } from "../../messages";
import { useBmapi } from "../../utils/bmapi-context";
import { getStatusLabel } from "../../utils/ecommerceUtils";
import { getErrorMessageString } from "../../utils/errors";
import FloatingActions from "../../ui/FloatingActions";

const useRowStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
  cardActionsRight: {
    justifyContent: "flex-end",
    marginTop: "1rem",
  },
  headerCheckbox: {
    padding: 0,
  },
  rowCheckbox: {},
  rightSpacer: {
    marginRight: theme.spacing(2),
  },
  rowToBeConfirmed: {
    backgroundColor: fade(theme.palette.primary.main, 0.2),
  },
  colSortable: {
    backgroundColor: "#f8f3f4",
  },
  detailValue: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

export default function ManageReservationsGen() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });

  const sortBy = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const classes = useRowStyles();
  const intl = useIntl();
  // traduce un valore delle costanti delle prenotazioni, se presente
  const formatOptional = (message) =>
    message ? intl.formatMessage(message) : "";
  // persone di riferimento con cellulare, separate da virgola solo se sono due
  const formatReferrers = (r) =>
    [
      [r.referrer, r.referrer_mobile],
      [r.referrer2, r.referrer2_mobile],
    ]
      .map((ref) => ref.filter(Boolean).join(" "))
      .filter(Boolean)
      .join(", ");
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const {
    bmapi,
    startLoading,
    stopLoading,
    notifyError,
    notifyDialog,
    hideDialog,
    notifySuccess,
  } = useBmapi();

  const [openRows, setOpenRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [notAvailableDays, setNotAvailableDays] = useState([]);
  const [reservationAvailableSlot, setReservationAvailableSlot] = useState([]);
  const [selection, setSelection] = useState([]);
  const [provinces, setProvinces] = useState({});
  const [countries, setCountries] = useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [reservationSelected, setReservationSelected] = React.useState(null);

  const initialValues = () => ({
    id: null,
    period_date: null,
    period: null,
    first_name: null,
    last_name: null,
    email: null,
    mobile: null,
    notes: null,
    country: "it",
    visitorType: "",
    groupSubtype: "",
    classNumber: "",
    classType: "",
    language: "",
    attrs: [],
  });
  const [values, setValues] = useState(initialValues());
  const { period_date, country } = values;

  const initFilters = {
    from: new Date(),
    fromStr: null,
    to: addMonths(new Date(), 12),
    created_at_from: null,
    created_at_to: null,
    toStr: null,
    status: "x",
    referrer: "",
    visitorType_filter: "",
    country: "",
    region: "",
    province: "",
  };
  const initCleanFilters = {
    from: null,
    fromStr: null,
    to: null,
    toStr: null,
    created_at_from: null,
    created_at_to: null,
    status: "x",
    referrer: "",
    visitorType_filter: "",
    country: "",
    region: "",
    province: "",
  };

  const [filters, setFilters] = useState(initFilters);

  const handleValue = useCallback((label) => {
    const updateValue = (val) => {
      setValues((v) => ({ ...v, [label]: val }));
    };

    return (i, f) => {
      if (typeof f === "boolean") updateValue(f);
      else if (i?.target) updateValue(i.target.value);
      else updateValue(i);
    };
  }, []);

  const handleClick = (event, reservation) => {
    setAnchorEl(event.currentTarget);
    setReservationSelected(reservation);
  };

  const businessID = bmapi.getUserInfo().business.id;

  const getFiltersParam = (filters) => {
    const filtersParam = {};

    const fields = {
      status: "status",
      referrer: "referrer",
      country: "attrs.country",
      region: "attrs.region",
      province: "attrs.province",
      visitorType_filter: "attrs.group_type",
    };

    Object.entries(fields).forEach(([key, param]) => {
      const value = filters[key];

      if (value && !(key === "status" && value === "x")) {
        filtersParam[param] = value;
      }
    });

    const dateFields = {
      from: "order_date_from",
      to: "order_date_to",
      created_at_from: "from",
      created_at_to: "to",
    };

    Object.entries(dateFields).forEach(([key, param]) => {
      if (filters[key]) {
        filtersParam[param] = format(filters[key], "dd-MM-yyyy");
      }
    });

    return filtersParam;
  };

  const loadReservationList = useCallback(
    async (filters = null) => {
      let filtersParam;

      if (filters) filtersParam = getFiltersParam(filters);

      startLoading();

      try {
        const resp = await bmapi.getEOrder({
          business: bmapi.getUserInfo().business.id,
          decode_user: true,
          ...filtersParam,
        });

        setReservations(resp || []);
        setSelection([]);
      } catch (e) {
        notifyError(getErrorMessageString(e, intl));
      } finally {
        stopLoading();
      }
    },
    [bmapi, intl, notifyError, startLoading, stopLoading]
  );

  useEffect(() => {
    getProvinces();
    getCountries();
    loadReservationList(filters);
  }, [bmapi, businessID, loadReservationList]);

  useEffect(() => {
    if (period_date != null) {
      setReservationAvailableSlot([]);
      doSearch();
    }
  }, [period_date]);

  useEffect(() => {
    if (country != "it") {
      cleanCountryData();
    }
  }, [country]);

  useEffect(() => {
    if (filters.country != "it") {
      cleanFilterCountryData();
    }
  }, [filters.country]);

  useEffect(() => {
    getAvailableDays();
  }, []);

  const searchReservations = useCallback(() => {
    loadReservationList(filters);
  }, [filters]);

  const cleanFilters = useCallback(() => {
    setFilters(initCleanFilters);
    loadReservationList();
  }, [setFilters, filters]);

  const toggleSelectionAll = useCallback(
    (checked) => {
      setSelection(checked ? reservations : []);
    },
    [reservations]
  );

  const handleFilters = useCallback((label) => {
    const updateValue = (val) => {
      let newVal = val;

      if (label === "from" || label === "to") {
        if (val) {
          newVal = new Date(val);
          newVal.setHours(0, 0, 0, 0);
        }
      }

      if (label === "from") {
        const fromStr = newVal ? newVal.toISOString() : null;

        setFilters((v) => ({
          ...v,
          from: newVal,
          fromStr,
        }));
      } else if (label === "to") {
        let toStr = null;

        if (newVal) {
          const toAfter = new Date(newVal);
          toAfter.setDate(toAfter.getDate() + 1);
          toStr = toAfter.toISOString();
        }

        setFilters((v) => ({
          ...v,
          to: newVal,
          toStr,
        }));
      } else {
        setFilters((v) => ({
          ...v,
          [label]: newVal,
        }));
      }
    };

    return (i, f) => {
      if (typeof f === "boolean") updateValue(f);
      else if (i?.target) updateValue(i.target.value);
      else updateValue(i);
    };
  }, []);

  const handleSelection = useCallback((reservation) => {
    setSelection((currentSelection) => {
      const selectedIndex = currentSelection.findIndex(
        (selected) => selected.id === reservation.id
      );

      if (selectedIndex === -1) {
        return [...currentSelection, reservation];
      }

      return currentSelection.filter((item) => item.id !== reservation.id);
    });
  }, []);

  const handleToggleRow = useCallback((index) => {
    setOpenRows((current) => {
      const newOpenRows = [...current];
      newOpenRows[index] = !newOpenRows[index];
      return newOpenRows;
    });
  }, []);

  const getAttributesMap = (reservation) => {
    const attributesMap = {};

    if (reservation.attrs && Array.isArray(reservation.attrs)) {
      reservation.attrs.forEach((attr) => {
        if (attr.key && attr.value) {
          attributesMap[attr.key] = attr.value;
        }
      });
    }

    return attributesMap;
  };

  const filterStatus = (status) => (item) => item.status === status;

  const confirmReservations = async (reservationsToConfirm) => {
    const ordersStatus = reservationsToConfirm.map((r) => ({
      id: r.id,
      status: 2,
    }));

    try {
      startLoading();
      await bmapi.updateOrdersListStatus(ordersStatus);
      hideDialog();
      loadReservationList(filters);
    } catch (e) {
      hideDialog();
      notifyError(getErrorMessageString(e, intl));
    } finally {
      stopLoading();
    }
  };

  const askConfirmReservations = () => {
    const toConfirm = selection.length
      ? selection.filter(filterStatus(0))
      : [reservationSelected];

    if (!toConfirm?.length) {
      notifyError(
        intl.formatMessage(ecommerce.noSelectedReservationsToConfirm)
      );
      return;
    }

    notifyDialog({
      text:
        toConfirm.length === 1
          ? intl.formatMessage(ecommerce.askConfirm1Reservation)
          : intl.formatMessage(ecommerce.askConfirmNReservations, {
              numReservations: toConfirm.length,
            }),
      title: intl.formatMessage(common.confirm),
      actions: [
        {
          text: intl.formatMessage(common.proceed),
          variant: "contained",
          color: "primary",
          onClick: () => confirmReservations(toConfirm),
        },
      ],
    });
  };

  const cancelReservations = async (reservationsToCancel) => {
    const ordersStatus = reservationsToCancel.map((r) => ({
      id: r.id,
      status: 0,
    }));

    try {
      startLoading();
      await bmapi.updateOrdersListStatus(ordersStatus);
      hideDialog();
      loadReservationList(filters);
    } catch (e) {
      hideDialog();
      notifyError(getErrorMessageString(e, intl));
    } finally {
      stopLoading();
    }
  };

  const askCancelReservations = () => {
    const toCancel = selection.filter(filterStatus(2));

    if (!toCancel?.length) {
      notifyError(intl.formatMessage(ecommerce.noSelectedReservationsToCancel));
      return;
    }

    notifyDialog({
      text:
        toCancel.length === 1
          ? intl.formatMessage(ecommerce.askCancel1Reservation)
          : intl.formatMessage(ecommerce.askCancelNReservations, {
              numReservations: toCancel.length,
            }),
      title: intl.formatMessage(common.cancel),
      actions: [
        {
          text: intl.formatMessage(common.proceed),
          variant: "contained",
          color: "primary",
          onClick: () => cancelReservations(toCancel),
        },
      ],
    });
  };

  const renderReservationDetails = (r) => {
    const attrs = getAttributesMap(r);

    const _visitorType =
      RESERVATIONS_VISITOR_TYPES_RESTRICTED[attrs["group_type"]];
    let visitorType = "";

    if (_visitorType) {
      try {
        visitorType = intl.formatMessage(_visitorType);
      } catch (e) {
        visitorType = _visitorType;
      }
    }

    const _visitLang = RESERVATIONS_VISIT_LANGUAGES[attrs["visit_lang"]];
    let visitLang = "";

    if (_visitLang) {
      try {
        visitLang = intl.formatMessage(_visitLang);
      } catch (e) {
        visitLang = _visitLang;
      }
    }

    return {
      attrs,
      visitorType,
      visitLang,
    };
  };

  const getAttrs = () => {
    const fields = {
      visitorType: "group_type",
      visitorGroupName: "group_name",
      groupSubtype: "group_subtype",
      classNumber: "class_number",
      classType: "class_type",
      laboratory: "laboratory",
      participantsNumber: "group_number",
      language: "visit_lang",
      country: "country",
      region: "region",
      province: "province",
      city: "city",
    };

    const attrs = Object.entries(fields)
      .filter(([field]) => values[field])
      .map(([field, key]) => ({
        key,
        value: values[field],
      }));

    if (values.survivor) {
      attrs.push({
        key: "survivor",
        value: "true",
      });
    }

    return attrs;
  };

  // con "Da confermare" è obbligatorio solo il tipo di visitatore,
  // altrimenti tutti i campi tranne referenti, telefoni e note
  const allFieldsRequired = !values.to_confirm;
  // regione, provincia e città sono obbligatorie solo per l'Italia
  const italianAddressRequired = allFieldsRequired && values.country == "it";

  const addReservations = () => {
    const data = {
      first_name: "fn",
      last_name: "ln",
      content_id: "default",
      date: format(values.period_date, "yyyy-MM-dd"),
      slot_id: values.period,
      email: bmapi.getUserInfo().email,
      notes: values.notes,
      status: values.to_confirm ? 0 : 2,
      referrer: values.referrer_person1,
      referrer_mobile: values.mobile1,
      referrer2: values.referrer_person2,
      referrer2_mobile: values.mobile2,
      attrs: getAttrs(),
    };
    bmapi
      .createEOrderAdmin(data)
      .then(() => {
        setOpen(false);
        loadReservationList(filters);
        notifySuccess(intl.formatMessage(common.addElement));
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(() => {
        stopLoading();
      });
  };

  const editReservations = () => {
    const data = {
      id: values.id,
      first_name: "fn",
      last_name: "ln",
      content_id: "default",
      date: format(values.period_date, "yyyy-MM-dd"),
      slot_id: values.period,
      mobile: values.mobile,
      email: bmapi.getUserInfo().email,
      notes: values.notes,
      referrer: values.referrer_person1,
      referrer_mobile: values.mobile1,
      referrer2: values.referrer_person2,
      referrer2_mobile: values.mobile2,
      attrs: getAttrs(),
    };
    bmapi
      .updateOrderAdmin(data)
      .then(() => {
        setOpen(false);
        loadReservationList(filters);
        notifySuccess(intl.formatMessage(common.editElement));
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(() => {
        stopLoading();
      });
  };

  const formatDate = (date) => {
    const strDate = `${date.day}-${date.month}-${date.year}`;
    const partialDate = strDate.split("-");
    const formattedDate = new Date(
      partialDate[2],
      partialDate[1] - 1, // index -1 for months
      partialDate[0]
    );
    return format(formattedDate, "yyyy-MM-dd");
  };

  const getAvailableDays = () => {
    const d = new Date();
    const from = format(d, "yyyy-MM-dd");
    const to = format(d.setFullYear(d.getFullYear() + 1), "yyyy-MM-dd");

    if (bmapi) {
      bmapi
        .getRevervationAvailableDays(
          {
            business: businessID,
          },
          {
            from: from,
            to: to,
          }
        )
        .then((resp) => {
          if (!resp) {
            resp = [];
          }
          const notAvailable = resp
            .filter((r) => r.status !== "AVAILABLE")
            .map((date) => formatDate(date));
          setNotAvailableDays(notAvailable);
          //setReservationAvailableDays(resp);
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    }
  };

  const doSearch = () => {
    const date = format(values.period_date, "yyyy-MM-dd");
    if (values.id == "") {
      removePeriod();
    }

    if (bmapi) {
      bmapi
        .getRevervationAvailableSlot({
          business: businessID,
          date: date,
        })
        .then((resp) => {
          if (!resp) {
            resp = [];
          }
          setReservationAvailableSlot(resp);
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    }
  };

  const getProvinces = () => {
    if (bmapi) {
      bmapi
        .getProvinces()
        .then((resp) => {
          if (!resp) {
            resp = [];
          }
          setProvinces(resp);
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    }
  };

  const getCountries = () => {
    const userLang = bmapi.language != "it" ? "en" : "it";
    if (bmapi) {
      bmapi
        .getCountries({
          language: userLang,
        })
        .then((resp) => {
          if (!resp) {
            resp = [];
          } else {
            resp = resp.items;
          }

          setCountries(resp);
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    }
  };

  const cleanCountryData = () => {
    const newValues = { ...values };
    newValues.region = null;
    newValues.province = null;
    setValues(newValues);
  };

  const cleanFilterCountryData = () => {
    const newValues = { ...filters };
    newValues.region = null;
    newValues.province = null;
    setFilters(newValues);
  };

  const removePeriod = () => {
    const newValues = { ...values };
    newValues.period = null;
    setValues(newValues);
  };

  const selectPeriod = (p) => {
    const newValues = { ...values };
    newValues.period = p?.inner_slot_id;
    setValues(newValues);
  };

  const setEditReservations = (r) => {
    let dataStr = r.date;
    dataStr = dataStr != "" ? dataStr : format(new Date(), "yyyy-MM-dd");
    const anno = parseInt(dataStr.substring(0, 4), 10);
    const mese = parseInt(dataStr.substring(4, 6), 10) - 1;
    const giorno = parseInt(dataStr.substring(6, 8), 10);
    const data = new Date(anno, mese, giorno);
    const attrs = getAttributesMap(r);

    setOpen(true);

    const newValues = { ...values };
    newValues.id = r.id;
    newValues.period_date = data;
    newValues.period = r.slot_id;
    newValues.first_name = r.first_name;
    newValues.last_name = r.last_name;
    newValues.email = r.email;
    newValues.mobile = r.mobile;
    newValues.notes = r.notes;
    newValues.referrer_person1 = r.referrer;
    newValues.referrer_person2 = r.referrer2;
    newValues.mobile1 = r.referrer_mobile;
    newValues.mobile2 = r.referrer2_mobile;
    newValues.visitorType = attrs["group_type"];
    newValues.visitorGroupName = attrs["group_name"];
    newValues.groupSubtype = attrs["group_subtype"];
    newValues.participantsNumber = attrs["group_number"];
    newValues.language = attrs["visit_lang"];
    newValues.country = attrs["country"];
    newValues.region = attrs["region"];
    newValues.province = attrs["province"];
    newValues.city = attrs["city"];
    newValues.survivor = attrs["survivor"];
    newValues.to_confirm = r.status == 0;
    setValues(newValues);
  };

  const openAddModal = () => {
    setOpen(true);
    setReservationAvailableSlot([]);
    setValues(initialValues);
  };

  const minDate = format(new Date(), "yyyy-MM-dd");

  const sortedReservations = [...reservations].sort((a, b) => {
    const valoreA = a[sortConfig.key];
    const valoreB = b[sortConfig.key];

    if (typeof valoreA === "string") {
      return sortConfig.direction === "asc"
        ? valoreA.localeCompare(valoreB)
        : valoreB.localeCompare(valoreA);
    }

    return sortConfig.direction === "asc"
      ? valoreA - valoreB
      : valoreB - valoreA;
  });

  function getSortIcon(field) {
    return (
      <span style={{ fontSize: "18px" }}>
        {sortConfig.key === field &&
          (sortConfig.direction === "asc" ? "↑" : "↓")}
      </span>
    );
  }
  return (
    <Container maxWidth="xl">
      <Title>{intl.formatMessage(ecommerce.reservationsDone)}</Title>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {intl.formatMessage(values.id == null ? common.add : common.modify)}
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            values.id == null ? addReservations() : editReservations();
          }}
        >
          <DialogContent>
            <DatePicker
              style={{ marginTop: 0 }}
              label={intl.formatMessage(common.date)}
              inputFormat="dd/MM/yyyy"
              value={values.period_date}
              onChange={handleValue("period_date")}
              format="dd/MM/yy"
              minDate={minDate}
              shouldDisableDate={(date) =>
                notAvailableDays.includes(format(date, "yyyy-MM-dd"))
              }
            />

            <Box style={{ marginTop: "10px" }}>
              {reservationAvailableSlot?.map((t) => (
                <Button
                  variant={
                    t.inner_slot_id == values.period ? "contained" : "outlined"
                  }
                  key={t.day}
                  style={{ marginRight: "10px" }}
                  onClick={() => {
                    selectPeriod(t);
                  }}
                >
                  {`${t.start.substring(0, 2)}:${t.start.substring(
                    2
                  )} - ${t.end.substring(0, 2)}:${t.end.substring(2)}`}
                </Button>
              ))}
            </Box>

            {values.period != null ? (
              <Box mt={2}>
                <TextField
                  required
                  value={values.visitorType}
                  fullWidth
                  onChange={handleValue("visitorType")}
                  label={intl.formatMessage(ecommerce.visitorType)}
                  select
                >
                  {Object.entries(RESERVATIONS_VISITOR_TYPES_RESTRICTED).map(
                    ([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {intl.formatMessage(value)}
                      </MenuItem>
                    )
                  )}
                </TextField>
                {/* Modificare etichetta anche per altre lingue */}
                <TextField
                  fullWidth
                  required={allFieldsRequired}
                  label={intl.formatMessage(ecommerce.visitorGroupName, {
                    type: values.visitorType?.includes("school")
                      ? "scuola"
                      : "gruppo",
                  })}
                  onChange={handleValue("visitorGroupName")}
                  value={values.visitorGroupName}
                  margin="dense"
                />

                <Box style={{ display: "flex", gap: 16 }}>
                  <TextField
                    style={{ flex: 1 }}
                    value={values.groupSubtype}
                    fullWidth
                    required={allFieldsRequired}
                    onChange={handleValue("groupSubtype")}
                    label={
                      values.visitorType?.includes("school")
                        ? intl.formatMessage(ecommerce.schoolType)
                        : intl.formatMessage(ecommerce.groupSubtype)
                    }
                    select
                    margin="dense"
                  >
                    {Object.entries(
                      values.visitorType?.includes("school")
                        ? RESERVATIONS_SCHOOL_TYPES
                        : RESERVATIONS_GROUP_TYPES
                    ).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {intl.formatMessage(value)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {values.visitorType?.includes("school") && (
                    <>
                      <TextField
                        style={{ flex: 1 }}
                        value={values.classType}
                        onChange={handleValue("classType")}
                        required={allFieldsRequired}
                        label={intl.formatMessage(ecommerce.classType)}
                        select
                        margin="dense"
                      >
                        {Object.entries(
                          values.groupSubtype === "middle_school"
                            ? RESERVATIONS_CLASS_TYPES_MIDDLE
                            : RESERVATIONS_CLASS_TYPES
                        ).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {intl.formatMessage(value)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        style={{ flex: 1 }}
                        label={intl.formatMessage(ecommerce.classNumber)}
                        value={values.classNumber}
                        onChange={handleValue("classNumber")}
                        required={allFieldsRequired}
                        type="number"
                        margin="dense"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, "");
                        }}
                      />
                    </>
                  )}
                </Box>

                <Box style={{ display: "flex", gap: 16 }}>
                  <TextField
                    margin="normal"
                    style={{ flex: 1 }}
                    label={intl.formatMessage(ecommerce.participantsNumber)}
                    onChange={handleValue("participantsNumber")}
                    value={values.participantsNumber}
                    required={allFieldsRequired}
                  />
                  <TextField
                    margin="normal"
                    style={{ flex: 1 }}
                    value={values.language}
                    fullWidth
                    required={allFieldsRequired}
                    onChange={handleValue("language")}
                    label={intl.formatMessage(ecommerce.visitLanguage)}
                    select
                  >
                    {Object.entries(RESERVATIONS_VISIT_LANGUAGES).map(
                      ([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {intl.formatMessage(value)}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Box>

                {values.visitorType?.includes("school") && (
                  <TextField
                    label={intl.formatMessage(ecommerce.laboratories)}
                    fullWidth
                    onChange={handleValue("laboratory")}
                    value={values.laboratory}
                    margin="dense"
                  />
                )}

                <Box>
                  <TextField
                    style={{ width: "25%" }}
                    value={values.country}
                    onChange={handleValue("country")}
                    required={allFieldsRequired}
                    label={intl.formatMessage(common.country)}
                    select
                    margin="dense"
                  >
                    <MenuItem key="country-empty" value="">
                      -
                    </MenuItem>
                    {countries?.sort().map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.str_value}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    style={{ width: "25%" }}
                    value={values.region}
                    onChange={handleValue("region")}
                    required={italianAddressRequired}
                    label={intl.formatMessage(form.region)}
                    disabled={values.country != "it"}
                    select
                    margin="dense"
                  >
                    <MenuItem key="region-empty" value="">
                      -
                    </MenuItem>
                    {[...new Set(Object.values(provinces))]
                      .sort()
                      .map((val) => (
                        <MenuItem key={val} value={val}>
                          {val}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    style={{ width: "25%" }}
                    value={values.province}
                    onChange={handleValue("province")}
                    required={italianAddressRequired}
                    label={intl.formatMessage(form.province)}
                    disabled={values.country != "it"}
                    select
                    margin="dense"
                  >
                    <MenuItem key="province-empty" value="">
                      -
                    </MenuItem>
                    {Object.entries(provinces)
                      .filter((key) => key.includes(values.region))
                      .sort()
                      .map(([key, value]) => (
                        <MenuItem key={key + value} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    style={{ width: "25%" }}
                    fullWidth
                    label={intl.formatMessage(form.city)}
                    onChange={handleValue("city")}
                    value={values.city}
                    required={italianAddressRequired}
                    margin="dense"
                  />
                </Box>
                <Box style={{ display: "flex", gap: 16 }}>
                  <TextField
                    style={{ flex: 1 }}
                    label={intl.formatMessage(ecommerce.referrerPerson) + "1"}
                    onChange={handleValue("referrer_person1")}
                    value={values.referrer_person1}
                    margin="dense"
                  />
                  <TextField
                    style={{ flex: 1 }}
                    fullWidth
                    label={intl.formatMessage(common.mobile) + "1"}
                    onChange={handleValue("mobile1")}
                    value={values.mobile1}
                    margin="dense"
                  />
                </Box>

                <Box style={{ display: "flex", gap: 16 }}>
                  <TextField
                    style={{ flex: 1 }}
                    label={intl.formatMessage(ecommerce.referrerPerson) + "2"}
                    onChange={handleValue("referrer_person2")}
                    value={values.referrer_person2}
                  />
                  <TextField
                    style={{ flex: 1 }}
                    fullWidth
                    label={intl.formatMessage(common.mobile) + "2"}
                    onChange={handleValue("mobile2")}
                    value={values.mobile2}
                  />
                </Box>
                <Box mt={1}>
                  {values.id == null ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.to_confirm}
                          onChange={handleValue("to_confirm")}
                          color="primary"
                        />
                      }
                      label={intl.formatMessage(ecommerce.toConfirm)}
                    />
                  ) : null}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.survivor}
                        onChange={handleValue("survivor")}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(ecommerce.survivor)}
                  />
                </Box>

                <TextareaAutosize
                  style={{ marginTop: "10px", width: "100%", height: "80px" }}
                  fullWidth
                  name="notes"
                  value={values.notes}
                  onChange={handleValue("notes")}
                  placeholder={intl.formatMessage(common.notes)}
                />
              </Box>
            ) : (
              ""
            )}

            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpen(false);
                }}
              >
                {intl.formatMessage(common.cancel)}
              </Button>
              {values.period != null ? (
                <Button type="submit" variant="contained" color="primary">
                  {intl.formatMessage(common.confirm)}
                </Button>
              ) : null}
            </DialogActions>
          </DialogContent>
        </form>
      </Dialog>

      <Container maxWidth="lg">
        <Box display="flex" flexDirection={smallScreen ? "column" : "row"}>
          <TextField
            margin="dense"
            value={filters.status}
            fullWidth
            onChange={handleFilters("status")}
            label={intl.formatMessage(ecommerce.status)}
            select
            InputLabelProps={{
              shrink: true,
            }}
          >
            {Array.from(RESERVATIONS_STATUS).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {intl.formatMessage(value)}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker
            fullWidth
            onChange={handleFilters("from")}
            label={intl.formatMessage(common.reservationFrom)}
            value={filters.from}
            format="dd/MM/yy"
            renderInput={(params) => <TextField {...params} />}
            clearable
          />

          <DatePicker
            fullWidth
            onChange={handleFilters("to")}
            label={intl.formatMessage(common.to)}
            value={filters.to}
            format="dd/MM/yy"
            renderInput={(params) => <TextField {...params} />}
            clearable
          />
          <DatePicker
            fullWidth
            onChange={handleFilters("created_at_from")}
            label={intl.formatMessage(common.requestFrom)}
            value={filters.created_at_from}
            format="dd/MM/yy"
            renderInput={(params) => <TextField {...params} />}
            clearable
          />
          <DatePicker
            fullWidth
            onChange={handleFilters("created_at_to")}
            label={intl.formatMessage(common.to)}
            value={filters.created_at_to}
            format="dd/MM/yy"
            renderInput={(params) => <TextField {...params} />}
            clearable
          />
        </Box>
        <Box display="flex" flexDirection={smallScreen ? "column" : "row"}>
          <TextField
            fullWidth
            label={intl.formatMessage(common.contactPerson)}
            onChange={handleFilters("referrer")}
            value={filters.referrer}
          />
          <TextField
            value={filters.country}
            onChange={handleFilters("country")}
            label={intl.formatMessage(common.country)}
            select
            fullWidth
          >
            <MenuItem key="filter-country-empty" value="">
              -
            </MenuItem>
            {countries?.sort().map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.str_value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            value={filters.country != "it" ? null : filters.region}
            fullWidth
            onChange={handleFilters("region")}
            label={intl.formatMessage(form.region)}
            disabled={filters.country != "it"}
            select
          >
            <MenuItem key="filter-region-empty" value="">
              -
            </MenuItem>
            {[...new Set(Object.values(provinces))].sort().map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            value={filters.province}
            fullWidth
            onChange={handleFilters("province")}
            label={intl.formatMessage(form.province)}
            disabled={filters.country != "it"}
            select
          >
            <MenuItem key="filter-province-empty" value="">
              -
            </MenuItem>
            {Object.entries(provinces)
              .filter((key) => key.includes(filters.region))
              .sort()
              .map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {key}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            value={filters.visitorType_filter}
            fullWidth
            onChange={handleFilters("visitorType_filter")}
            label={intl.formatMessage(ecommerce.visitorType)}
            select
          >
            <MenuItem key="filter-visitor-type-empty" value="">
              -
            </MenuItem>
            {Object.entries(RESERVATIONS_VISITOR_TYPES_RESTRICTED).map(
              ([key, value]) => (
                <MenuItem key={key} value={key}>
                  {intl.formatMessage(value)}
                </MenuItem>
              )
            )}
          </TextField>
        </Box>
        <Box style={{ marginTop: "10px" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={searchReservations}
            style={{ marginRight: "10px" }}
          >
            {intl.formatMessage(common.search)}
          </Button>
          <Button variant="outlined" color="primary" onClick={cleanFilters}>
            {intl.formatMessage(ecommerce.cleanFilters)}
          </Button>
        </Box>
      </Container>

      {!smallScreen && (
        <Box mt={5}>
          <Card>
            <CardContent>
              <TableContainer>
                <Table padding="none">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        className={classes.headerCheckbox}
                      >
                        <Checkbox
                          indeterminate={
                            selection.length > 0 &&
                            selection.length < reservations.length
                          }
                          checked={
                            reservations.length > 0 &&
                            selection.length === reservations.length
                          }
                          onChange={(e) => toggleSelectionAll(e.target.checked)}
                        />
                      </TableCell>

                      <TableCell />
                      <TableCell
                        className={classes.colSortable}
                        style={{ cursor: "pointer" }}
                        onClick={() => sortBy("status")}
                      >
                        {intl.formatMessage(common.status)}
                        {getSortIcon("status")}
                      </TableCell>
                      <TableCell
                        className={classes.colSortable}
                        style={{ cursor: "pointer" }}
                        onClick={() => sortBy("date")}
                      >
                        {intl.formatMessage(common.dateAndTimeReservationShort)}{" "}
                        {getSortIcon("date")}
                      </TableCell>
                      <TableCell>{intl.formatMessage(common.name)}</TableCell>
                      <TableCell
                        className={classes.colSortable}
                        style={{ cursor: "pointer" }}
                        onClick={() => sortBy("created_at")}
                      >
                        {intl.formatMessage(common.dateAndTimeRequestShort)}
                        {getSortIcon("created_at")}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage(ecommerce.schoolOrGroupType)}
                      </TableCell>
                      <TableCell>{intl.formatMessage(form.city)}</TableCell>
                      <TableCell>{intl.formatMessage(form.province)}</TableCell>
                      <TableCell>{intl.formatMessage(form.region)}</TableCell>
                      <TableCell>
                        {intl.formatMessage(ecommerce.laboratories)}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage(ecommerce.classNumber)}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage(ecommerce.classType)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  {sortedReservations.map((r, index) => {
                    const { attrs, visitLang } = renderReservationDetails(r);

                    const plannedDeliveryDate = new Date(r.planned_delivery);
                    const isPastReservation = plannedDeliveryDate < today;

                    return (
                      <TableBody key={r.id}>
                        <TableRow
                          className={`${classes.root} ${
                            r.status == 0 ? classes.rowToBeConfirmed : ""
                          }`}
                          style={
                            isPastReservation
                              ? { background: "#f2f2f2" }
                              : undefined
                          }
                        >
                          <TableCell
                            padding="checkbox"
                            className={classes.rowCheckbox}
                            align="center"
                          >
                            <Checkbox
                              edge="start"
                              checked={selection.some(
                                (item) => item.id === r.id
                              )}
                              onChange={() => handleSelection(r)}
                              color="primary"
                              disabled={isPastReservation}
                            />
                          </TableCell>

                          <TableCell padding="none">
                            <IconButton
                              hidden
                              aria-label="expand row"
                              size="small"
                              onClick={() => handleToggleRow(index)}
                              disabled={false}
                            >
                              {openRows[index] ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </TableCell>

                          <TableCell padding="none">
                            {getStatusLabel(r.status, intl)}
                          </TableCell>

                          <TableCell padding="none">
                            <Typography>
                              {format(plannedDeliveryDate, "dd/MM/yy HH.mm")}
                            </Typography>
                          </TableCell>

                          <TableCell padding="none">
                            {attrs["group_name"]}
                          </TableCell>

                          <TableCell padding="none">
                            <Typography>
                              {format(new Date(r.created_at), "dd/MM/yy HH.mm")}
                            </Typography>
                          </TableCell>

                          <TableCell padding="none">
                            {formatOptional(
                              RESERVATIONS_SCHOOL_TYPES[
                                attrs["group_subtype"]
                              ] ||
                                RESERVATIONS_GROUP_TYPES[attrs["group_subtype"]]
                            )}
                          </TableCell>
                          <TableCell padding="none">{attrs["city"]}</TableCell>
                          <TableCell padding="none">
                            {attrs["province"]}
                          </TableCell>
                          <TableCell padding="none">
                            {attrs["region"]}
                          </TableCell>
                          <TableCell padding="none">
                            {attrs["laboratory"] || "-"}
                          </TableCell>
                          <TableCell padding="none">
                            {attrs["class_number"] || "-"}
                          </TableCell>

                          <TableCell padding="none">
                            {formatOptional(
                              RESERVATIONS_CLASS_TYPES[attrs["class_type"]]
                            ) || "-"}
                          </TableCell>

                          <TableCell padding="none">
                            <IconButton onClick={(e) => handleClick(e, r)}>
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            style={{
                              paddingBottom: 0,
                              paddingTop: 0,
                            }}
                            colSpan={13}
                          >
                            <Collapse
                              in={openRows[index]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box margin={1}>
                                {r.mobile != undefined ? (
                                  <Typography>
                                    {intl.formatMessage(common.mobile) +
                                      " prenotazione:"}{" "}
                                    <span className={classes.detailValue}>
                                      {r.mobile}
                                    </span>
                                  </Typography>
                                ) : null}
                                {r.email != undefined ? (
                                  <Typography>
                                    {intl.formatMessage(common.email)}{" "}
                                    prenotazione:{" "}
                                    <Link
                                      style={{
                                        color: "black",
                                        textDecoration: "underline",
                                      }}
                                      href={`mailto:${r.email}`}
                                    >
                                      <span className={classes.detailValue}>
                                        {r.email}
                                      </span>
                                    </Link>
                                  </Typography>
                                ) : null}
                                {attrs["survivor"] != undefined ? (
                                  <Typography>
                                    {intl.formatMessage(ecommerce.survivor)}:{" "}
                                    <span className={classes.detailValue}>
                                      {intl.formatMessage(
                                        attrs["survivor"]
                                          ? common.yes
                                          : common.no
                                      )}
                                    </span>
                                  </Typography>
                                ) : null}
                                {visitLang != undefined ? (
                                  <Typography>
                                    {intl.formatMessage(
                                      ecommerce.visitLanguage
                                    )}
                                    :{" "}
                                    <span className={classes.detailValue}>
                                      {visitLang}
                                    </span>
                                  </Typography>
                                ) : null}
                                {r.notes != "" ? (
                                  <Typography>
                                    {intl.formatMessage(common.notes)}:{" "}
                                    <span className={classes.detailValue}>
                                      {r.notes}
                                    </span>
                                  </Typography>
                                ) : null}
                                <Typography>
                                  {intl.formatMessage(ecommerce.referrerPerson)}
                                  :{" "}
                                  <span className={classes.detailValue}>
                                    {formatReferrers(r)}
                                  </span>
                                </Typography>
                                {attrs["country"] != undefined ? (
                                  <Typography>
                                    {intl.formatMessage(common.country)}:{" "}
                                    <span className={classes.detailValue}>
                                      {attrs["country"]?.toUpperCase()}
                                    </span>
                                  </Typography>
                                ) : null}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    );
                  })}
                </Table>
              </TableContainer>
              <CardActions className={classes.cardActionsRight}>
                <Button variant="outlined" color="primary" onClick={() => {}}>
                  {intl.formatMessage(ecommerce.exportXls)}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={askConfirmReservations}
                  disabled={!selection?.length}
                >
                  {intl.formatMessage(common.confirm)}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={askCancelReservations}
                  disabled={!selection?.length}
                >
                  {intl.formatMessage(common.cancel)}
                </Button>
              </CardActions>
            </CardContent>
          </Card>
        </Box>
      )}

      {smallScreen && (
        <Box mt={5}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Checkbox
                  indeterminate={
                    selection.length > 0 &&
                    selection.length < reservations.length
                  }
                  checked={
                    reservations.length > 0 &&
                    selection.length === reservations.length
                  }
                  onChange={(e) => toggleSelectionAll(e.target.checked)}
                />

                <Typography>
                  {intl.formatMessage(common.selectDeselectAll)}
                </Typography>
              </Box>

              <List>
                {sortedReservations.map((r, index) => {
                  const { attrs, visitLang } = renderReservationDetails(r);

                  const plannedDeliveryDate = new Date(r.planned_delivery);
                  const isPastReservation = plannedDeliveryDate < today;

                  return (
                    <React.Fragment key={r.id}>
                      {index !== 0 && <Divider component="li" />}

                      <ListItem
                        className={
                          r.status == 0 ? classes.rowToBeConfirmed : ""
                        }
                        style={
                          isPastReservation
                            ? { background: "#f2f2f2" }
                            : undefined
                        }
                      >
                        <ListItemText
                          disableTypography
                          primary={
                            <React.Fragment>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  edge="start"
                                  checked={selection.some(
                                    (item) => item.id === r.id
                                  )}
                                  onChange={() => handleSelection(r)}
                                  color="primary"
                                  disabled={isPastReservation}
                                />

                                <Typography
                                  style={{ fontWeight: 700, flexGrow: 1 }}
                                >
                                  {format(
                                    plannedDeliveryDate,
                                    "dd/MM/yy HH.mm"
                                  )}
                                </Typography>

                                <IconButton onClick={(e) => handleClick(e, r)}>
                                  <MoreVert />
                                </IconButton>
                              </Box>

                              <Typography>
                                {intl.formatMessage(common.status)}:{" "}
                                <span className={classes.detailValue}>
                                  {getStatusLabel(r.status, intl)}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(common.name)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["group_name"]}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(
                                  common.dateAndTimeRequestShort
                                )}
                                :{" "}
                                <span className={classes.detailValue}>
                                  {format(
                                    new Date(r.created_at),
                                    "dd/MM/yy HH.mm"
                                  )}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(
                                  ecommerce.schoolOrGroupType
                                )}
                                :{" "}
                                <span className={classes.detailValue}>
                                  {formatOptional(
                                    RESERVATIONS_SCHOOL_TYPES[
                                      attrs["group_subtype"]
                                    ] ||
                                      RESERVATIONS_GROUP_TYPES[
                                        attrs["group_subtype"]
                                      ]
                                  )}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(form.city)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["city"]}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(form.province)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["province"]}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(form.region)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["region"]}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(ecommerce.laboratories)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["laboratory"] || "-"}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(ecommerce.classNumber)}:{" "}
                                <span className={classes.detailValue}>
                                  {attrs["class_number"] || "-"}
                                </span>
                              </Typography>
                              <Typography>
                                {intl.formatMessage(ecommerce.classType)}:{" "}
                                <span className={classes.detailValue}>
                                  {formatOptional(
                                    RESERVATIONS_CLASS_TYPES[
                                      attrs["class_type"]
                                    ]
                                  ) || "-"}
                                </span>
                              </Typography>

                              <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => handleToggleRow(index)}
                              >
                                {openRows[index] ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>

                              <Collapse
                                in={openRows[index]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box margin={1}>
                                  {r.mobile != undefined ? (
                                    <Typography>
                                      {intl.formatMessage(common.mobile) +
                                        " prenotazione:"}{" "}
                                      <span className={classes.detailValue}>
                                        {r.mobile}
                                      </span>
                                    </Typography>
                                  ) : null}
                                  {r.email != undefined ? (
                                    <Typography>
                                      {intl.formatMessage(common.email)}{" "}
                                      prenotazione:{" "}
                                      <Link
                                        style={{
                                          color: "black",
                                          textDecoration: "underline",
                                        }}
                                        href={`mailto:${r.email}`}
                                      >
                                        <span className={classes.detailValue}>
                                          {r.email}
                                        </span>
                                      </Link>
                                    </Typography>
                                  ) : null}
                                  {attrs["survivor"] != undefined ? (
                                    <Typography>
                                      {intl.formatMessage(ecommerce.survivor)}:{" "}
                                      <span className={classes.detailValue}>
                                        {intl.formatMessage(
                                          attrs["survivor"]
                                            ? common.yes
                                            : common.no
                                        )}
                                      </span>
                                    </Typography>
                                  ) : null}
                                  {visitLang != undefined ? (
                                    <Typography>
                                      {intl.formatMessage(
                                        ecommerce.visitLanguage
                                      )}
                                      :{" "}
                                      <span className={classes.detailValue}>
                                        {visitLang}
                                      </span>
                                    </Typography>
                                  ) : null}
                                  {r.notes != "" ? (
                                    <Typography>
                                      {intl.formatMessage(common.notes)}:{" "}
                                      <span className={classes.detailValue}>
                                        {r.notes}
                                      </span>
                                    </Typography>
                                  ) : null}
                                  <Typography>
                                    {intl.formatMessage(
                                      ecommerce.referrerPerson
                                    )}
                                    :{" "}
                                    <span className={classes.detailValue}>
                                      {formatReferrers(r)}
                                    </span>
                                  </Typography>
                                  {attrs["country"] != undefined ? (
                                    <Typography>
                                      {intl.formatMessage(common.country)}:{" "}
                                      <span className={classes.detailValue}>
                                        {attrs["country"]?.toUpperCase()}
                                      </span>
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Collapse>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
              <CardActions className={classes.cardActionsRight}>
                <Button variant="outlined" color="primary" onClick={() => {}}>
                  {intl.formatMessage(ecommerce.exportXls)}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={askConfirmReservations}
                  disabled={!selection?.length}
                >
                  {intl.formatMessage(common.confirm)}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={askCancelReservations}
                  disabled={!selection?.length}
                >
                  {intl.formatMessage(common.cancel)}
                </Button>
              </CardActions>
            </CardContent>
          </Card>
        </Box>
      )}

      <Menu
        key={reservationSelected}
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setReservationSelected(null);
        }}
        onClick={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setEditReservations(reservationSelected);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">
            {intl.formatMessage(common.modify)}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={askConfirmReservations}
          disabled={new Date(reservationSelected?.planned_delivery) < today}
        >
          <ListItemIcon>
            <Check fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">
            {intl.formatMessage(common.confirm)}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={askCancelReservations}
          disabled={reservationSelected?.status !== 2}
        >
          <ListItemIcon>
            <Cancel fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">
            {intl.formatMessage(common.cancel)}
          </Typography>
        </MenuItem>
      </Menu>

      <FloatingActions>
        <Fab variant="extended" color="primary" onClick={openAddModal}>
          <Add />
          {intl.formatMessage(common.add)}
        </Fab>
      </FloatingActions>
    </Container>
  );
}
